import React, { useEffect, useRef, useState } from "react";

// Progressive-enhancement reveal for the story site.
//
// WHY THIS EXISTS (a real defect, measured 2026-07-31).
// framer-motion writes its `initial` state as an INLINE STYLE, and this site is
// SSR-prerendered, so `initial={{ opacity: 0 }}` ships `opacity:0` in the static
// HTML. Content then cannot paint until React hydrates, and never paints at all
// if JS fails. The live site does this today: dist/index.html contains 36 such
// elements including the home page H1, /how-it-works 15, /communities 11.
//
// So the default here is VISIBLE. The hidden-then-revealed behaviour is opt-in
// via the `js` class index.html sets before first paint, which means no flash
// and a correct no-JS rendering.
//
// WHY A SHARED SCROLL REGISTRY AND NOT IntersectionObserver.
// The first two attempts used IO and both shipped content stuck at opacity 0:
//   1. plain IO: an observer reports CHANGES, and its first callback for an
//      element already ABOVE the viewport is `isIntersecting: false`. If
//      hydration lands after the reader has scrolled past (fast scroll,
//      restored position, #hash link, slow bundle), it never intersects again.
//   2. IO plus an on-mount "already past?" check: better, but still left
//      headings at opacity 0 on repeat runs, because IO callbacks are async and
//      an element measured mid-layout (before fonts settle) can have zero area,
//      which never satisfies a non-zero threshold.
//
//   3. a shared scroll registry that added the class with `classList.add`:
//      deterministic about WHEN to reveal, but React owns `className` on this
//      element, so the very next re-render anywhere up the tree rebuilt the
//      attribute WITHOUT `is-in` and silently re-hid the content. The Navbar
//      sets state on scroll, so that re-render happens on any real page.
//      Revealed state therefore lives in React state, never in classList.
//
// All three failures are the SAME defect this component exists to prevent, so
// the mechanism is now deterministic rather than event-driven: one shared passive
// scroll/resize listener, rAF-batched, that checks every pending element's
// rect. No async callback, no thresholds, no zero-area trap. It also settles
// after `load` and after `document.fonts.ready`, the two moments layout is
// known to shift. Cost is one listener and a linear pass over a handful of
// nodes, which is nothing next to silently invisible copy.

type Pending = { el: HTMLElement; show: () => void };

const pending = new Set<Pending>();
let wired = false;
let frame = 0;

function flush() {
  frame = 0;
  const limit = window.innerHeight - 60; // reveal slightly before fully in view
  for (const item of Array.from(pending)) {
    if (!item.el.isConnected) {
      pending.delete(item);
      continue;
    }
    // `top < limit` is true both for "in view" AND for "already scrolled past"
    // (where top is negative), which is the case the observer versions missed.
    if (item.el.getBoundingClientRect().top < limit) {
      item.show();
      pending.delete(item);
    }
  }
}

function schedule() {
  if (frame) return;
  frame = requestAnimationFrame(flush);
}

// Scroll EVENTS are not a reliable trigger on this site: App.tsx wraps every
// page in <SmoothScroll>, which runs Lenis, and Lenis hijacks native scrolling.
// Programmatic and momentum scrolling under it do not produce the plain scroll
// events this relied on, which showed up as reveals resolving inconsistently
// (three separate measurements of the same page gave 1, 0 and 3 stuck
// elements). A reader who scrolls fast could land on invisible copy.
//
// So the trigger does not depend on events at all. While anything is pending,
// a rAF loop re-checks every frame; the moment the set empties the loop stops
// dead, so a settled page costs nothing. The listeners below stay as a cheap
// way to restart it when the page changes shape later.
let pumping = false;
function pump() {
  if (!pending.size) {
    pumping = false;
    return;
  }
  flush();
  requestAnimationFrame(pump);
}
function startPump() {
  if (pumping) return;
  pumping = true;
  requestAnimationFrame(pump);
}

function wire() {
  if (wired || typeof window === "undefined") return;
  wired = true;
  window.addEventListener("scroll", startPump, { passive: true });
  window.addEventListener("resize", startPump, { passive: true });
  window.addEventListener("load", startPump);
  // Fonts change line counts, which changes every rect below them.
  if (document.fonts?.ready)
    document.fonts.ready.then(schedule).catch(() => {});
}

type Props = {
  children: React.ReactNode;
  /** Vertical travel in px. */
  y?: number;
  /** Stagger in ms. */
  delay?: number;
  className?: string;
  as?: "div" | "section" | "li";
};

const Reveal: React.FC<Props> = ({
  children,
  y = 18,
  delay = 0,
  className = "",
  as = "div",
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const item: Pending = { el, show: () => setShown(true) };
    pending.add(item);
    wire();
    startPump();

    return () => {
      pending.delete(item);
    };
  }, []);

  const Tag = as as React.ElementType;
  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "is-in" : ""} ${className}`}
      style={
        {
          "--reveal-y": `${y}px`,
          "--reveal-delay": `${delay}ms`,
        } as React.CSSProperties
      }
    >
      {children}
    </Tag>
  );
};

export default Reveal;
